import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import StickyActions from '@/components/StickyActions.jsx';
import { Field } from '@/components/Field.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { SET_POLITICA } from './gql.js';
import { LIST_CUENTAS } from '../Cuentas/gql.js';
import { LIST_CENTROS } from '../Centros/gql.js';

const moduloOptions = [
  { value: 'ventas', label: 'Ventas' },
  { value: 'compras', label: 'Compras' },
  { value: 'tesoreria', label: 'Tesorería' },
  { value: 'stock', label: 'Stock' },
];

export default function PoliticaForm({ politica, empresaId, onSuccess, onCancel }) {
  const toast = useToast();
  const [form, setForm] = useState({
    modulo: 'ventas',
    evento: '',
    cuentaIdDebe: '',
    cuentaIdHaber: '',
    centroId: '',
    activo: true,
  });
  const [, setPolitica] = useMutation(SET_POLITICA);
  const [cuentasResult] = useQuery({ query: LIST_CUENTAS, variables: { limit: 500, offset: 0 } });
  const [centrosResult] = useQuery({ query: LIST_CENTROS, variables: { empresaId }, pause: !empresaId });

  useEffect(() => {
    if (politica) {
      setForm({
        modulo: politica.modulo,
        evento: politica.evento,
        cuentaIdDebe: politica.cuentaIdDebe ?? '',
        cuentaIdHaber: politica.cuentaIdHaber ?? '',
        centroId: politica.centroId ?? '',
        activo: politica.activo !== false,
      });
    } else {
      setForm({ modulo: 'ventas', evento: '', cuentaIdDebe: '', cuentaIdHaber: '', centroId: '', activo: true });
    }
  }, [politica]);

  const cuentas = cuentasResult.data?.listCuentas?.items ?? [];
  const centros = useMemo(() => flattenCentros(centrosResult.data?.listCentros ?? []), [centrosResult.data]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.evento.trim()) {
      toast.error('Indicá un evento.');
      return;
    }
    try {
      const result = await setPolitica({
        input: {
          empresaId,
          modulo: form.modulo,
          evento: form.evento.trim(),
          cuentaIdDebe: form.cuentaIdDebe || null,
          cuentaIdHaber: form.cuentaIdHaber || null,
          centroId: form.centroId || null,
          activo: form.activo,
        },
      });
      if (result.error) throw result.error;
      toast.success('Política guardada');
      onSuccess?.();
    } catch (error) {
      toast.error(error.message ?? 'No fue posible guardar la política');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.9)] p-6 shadow-[0_20px_36px_-24px_rgba(var(--shadow),0.35)]">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Field label="Módulo">
              <select
                value={form.modulo}
                onChange={(event) => handleChange('modulo', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {moduloOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Evento">
              <input
                value={form.evento}
                onChange={(event) => handleChange('evento', event.target.value)}
                placeholder="factura_emitida, pago_proveedor..."
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Cuenta Debe">
              <select
                value={form.cuentaIdDebe}
                onChange={(event) => handleChange('cuentaIdDebe', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Sin asignar</option>
                {cuentas.map((cuenta) => (
                  <option key={cuenta.id} value={cuenta.id}>
                    {cuenta.codigo} · {cuenta.nombre}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Cuenta Haber">
              <select
                value={form.cuentaIdHaber}
                onChange={(event) => handleChange('cuentaIdHaber', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Sin asignar</option>
                {cuentas.map((cuenta) => (
                  <option key={cuenta.id} value={cuenta.id}>
                    {cuenta.codigo} · {cuenta.nombre}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Centro de costo">
              <select
                value={form.centroId}
                onChange={(event) => handleChange('centroId', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Sin centro</option>
                {centros.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} · {centro.nombre}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Estado">
              <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(event) => handleChange('activo', event.target.checked)}
                  className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                />
                Política activa
              </label>
            </Field>
          </div>
        </div>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
          >
            Guardar política
          </button>
        }
      />
    </form>
  );
}

function flattenCentros(nodes, acc = []) {
  for (const node of nodes) {
    acc.push({ id: node.id, codigo: node.codigo, nombre: node.nombre });
    if (node.hijos?.length) {
      flattenCentros(node.hijos, acc);
    }
  }
  return acc;
}
